import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import { AnexoSolicitacao } from "model/solicitacao/AnexoSolicitacao";
import { Situacao } from "model/solicitacao/Situacao";
import { SolicitacaoDetalhada, SolicitacaoDetalhadaCadastroDTO, SolicitacaoDetalhadaCadastroFormulario } from "model/solicitacao/SolicitacaoDetalhada";
import { SolicitacaoResumida } from "model/solicitacao/SolicitacaoResumida";
import { ApiAnexoSolicitacao } from "service/api/solicitacao/ApiAnexoSolicitacao";
import { ApiSolicitacoes } from "service/api/solicitacao/ApiSolicitacoes";
import { RestricoesCamposFormulario } from "service/restricao/RestricoesCamposFormulario";
import { ErrosUtils } from "util/ErrosUtils";
import { MarkdownUtil } from "util/markdown/MarkdownUtil";
import { PatchData } from "util/PatchData";


interface VisualizacaoCarregarAsyncThunk {
  idSolicitacao: number;
  /**
   * Utilizada no estado pending
   */
  solicitacao?: SolicitacaoResumida
}

interface EdicaoPersistirAsyncThunk {
  /** Solicitação editada */
  solicitacao: SolicitacaoDetalhada;
  /**
   * Campos que foram alterados
   */
  edicoes: any
}

class SolicitacaoCorrenteVisualizacaoAction {
  static readonly carregar = createAsyncThunk(
    'SolicitacaoCorrente/visualizar/carregar',
    async ({idSolicitacao}: VisualizacaoCarregarAsyncThunk, {rejectWithValue}) => {
      try {
        return await ApiSolicitacoes.getSolicitacao(idSolicitacao);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  )
    
  static readonly atualizar = createAction<SolicitacaoResumida>('SolicitacaoCorrente/visualizar/atualizar');

}

class SolicitacaoCorrenteCriacaoAction {
  /** Inicia o modo de criação */
  static readonly iniciar = createAction('SolicitacaoCorrente/criacao/iniciar');

  /** Persiste a solicitação como uma nova */
  static readonly persistir = createAsyncThunk(
    'SolicitacaoCorrente/criacao/persistir',
    async (solicitacao: SolicitacaoDetalhadaCadastroFormulario, {rejectWithValue}) => {
      const anexos = solicitacao.extra?.anexos ?? [];

      const descricao = solicitacao.descricao?.trim() ?? ""
      const descricaoMarkdown = MarkdownUtil.toMarkdown(descricao);

      const solicitacaoASerPersistida: SolicitacaoDetalhadaCadastroDTO = {
        ...solicitacao,
        descricao: descricaoMarkdown,
        idsAnexos: anexos
          .filter(it => it.serverId)
          .map(it => it.serverId) ?? []
      }

      try {
        return await ApiSolicitacoes.novaSolicitacao(solicitacaoASerPersistida);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  )
}

class SolicitacaoCorrenteEdicaoAction {
  /** Inicia o modo de edição */
  static readonly iniciar = createAction('SolicitacaoCorrente/edicao/iniciar');

  /** Altera a situação da solicitação corrente para a indicada e salva */
  static readonly atualizarSituacao = (solicitacao: SolicitacaoDetalhada, novaSituacao: Situacao) => {
    const edicao = {
      solicitacao: solicitacao,
      edicoes: {
        situacao: novaSituacao
      }
    }
    return SolicitacaoCorrenteEdicaoAction._realizarEdicao(edicao);
  }

  /**
   * Atualizar todos os campos em que se detectou-se uma mudança dos valores
   */
  static readonly persistir = (solicitacaoAtualizada: SolicitacaoDetalhada, solicitacaoAtual: SolicitacaoDetalhada, restricoes: Readonly<RestricoesCamposFormulario>) => {
    const mudancas = new PatchData(restricoes).gerar(solicitacaoAtualizada, solicitacaoAtual);
    const edicao = {
      solicitacao: solicitacaoAtual,
      edicoes: mudancas
    }
    
    return SolicitacaoCorrenteEdicaoAction._realizarEdicao(edicao);
  }
  

  /** Cancela o processo de edição, voltando para o estado de visualização */
  static readonly cancelar = createAction('SolicitacaoCorrente/edicao/cancelar');

  /** Quem realmente persiste */
  static readonly _realizarEdicao = createAsyncThunk(
    'SolicitacaoCorrente/edicao/persistirSituacao',
    async (edicao: EdicaoPersistirAsyncThunk, {rejectWithValue}) => {
      edicao.edicoes['dataAtualizacao'] = edicao.solicitacao.dataAtualizacao;

      try {
        return await ApiSolicitacoes.editarSolicitacao(edicao.solicitacao.id, edicao.edicoes);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  )
}


class SolicitacaoCorrenteAnexosAction {
  static readonly adicionarNaLista = createAction('SolicitacaoCorrente/anexos/adicionarNaLista',
    (anexos: AnexoSolicitacao[]) => {
    return {
      payload: {
        anexos: anexos,
      }
    }
  });

  static readonly carregar = createAsyncThunk(
    'SolicitacaoCorrente/anexos/carregar',
    async (idSolicitacao: number, {rejectWithValue}) => {
      try {
        return await ApiAnexoSolicitacao.findAnexos(idSolicitacao);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  )
}


export class SolicitacaoCorrenteAction {
  static readonly visualizacao = SolicitacaoCorrenteVisualizacaoAction
  static readonly edicao = SolicitacaoCorrenteEdicaoAction
  static readonly criacao = SolicitacaoCorrenteCriacaoAction

  static readonly anexos = SolicitacaoCorrenteAnexosAction
}
